import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
import { useAuth } from "context/Auth"
import { escapeRegExp } from "lodash"
import { DEFAULT_TAG } from "pages/discover"
import { type DashboardWithTag } from "../views/types"
import { DashboardEmptyState } from "./DashboardEmptyState"
import { DashboardGrid } from "./DashboardGrid"

type DashboardGridWithSearchProps = {
  search: string
  tag: string
  setSearch: (search: string) => void
  dashboards: DashboardWithTag[]
}

/**
 * String matching which matches the string exactly if the keyword is in quotes
 * or uses case insensitive matching otherwise
 */
const quotedSearchQueryMatch = (value: string, search: string) => {
  // Match the string exactly if the keyword is in quotes
  if (search.startsWith('"') && search.endsWith('"')) {
    const exactSearch = search.slice(1, -1)
    const index = value.indexOf(exactSearch)
    if (index === -1) return false

    // Ensure the exact match is a standalone word
    const escapedSearch = escapeRegExp(exactSearch)
    const wordBoundaryRegex = new RegExp(`\\b${escapedSearch}\\b`, "i")
    return wordBoundaryRegex.test(value)
  }

  return value.toLowerCase().includes(search.toLowerCase())
}

const dashboardNameSearch = (dashboard: DashboardWithTag, search: string) => {
  return (
    quotedSearchQueryMatch(dashboard.name ?? "", search) ||
    quotedSearchQueryMatch(dashboard.description ?? "", search) ||
    search.toLowerCase() === ""
  )
}

const dashboardTagSearch = (dashboard: DashboardWithTag, tag: string) => {
  return (
    dashboard.tags.map((tag) => tag.id).includes(tag) || tag === DEFAULT_TAG
  )
}

export function DashboardGridWithSearch({
  search,
  tag,
  setSearch,
  dashboards,
}: DashboardGridWithSearchProps) {
  const { user, isLoginFinished } = useAuth()
  const isLoggedIn = Boolean(user?.id && isLoginFinished)

  const filteredDashboards = dashboards
    .filter((dashboard) => {
      return (
        dashboardNameSearch(dashboard, search) &&
        dashboardTagSearch(dashboard, tag)
      )
    })
    .sort((a, b) => {
      if (isLoggedIn) {
        return 0
      }
      if (a.login_required > b.login_required) {
        return 1
      }
      if (a.login_required < b.login_required) {
        return -1
      }
      return 0
    })

  return (
    <div className="flex flex-col gap-5 flex-1 justify-start items-start">
      <SearchField
        placeholder="Search"
        value={search}
        onChange={setSearch}
        className="w-full"
      />
      {filteredDashboards.length === 0 ? (
        <DashboardEmptyState tag={tag} search={search} />
      ) : (
        <DashboardGrid
          dashboards={filteredDashboards}
          isLoading={!isLoginFinished}
          isLoggedIn={isLoggedIn}
        />
      )}
    </div>
  )
}
